import { FileTypeEnum } from 'api/enums';
import { IFeature, IFeatureFile, IFeatureGuideline } from 'api/models';
import { BackButton, Button } from 'library/buttons';
import { useEffect, useRef, useState } from 'react';
import { getFeatureTypeLabel } from '../feature-announcement.utils';

const Media = ({ file, title, className }: { file: IFeatureFile; title: string; className: string }) =>
    file.type === FileTypeEnum.VIDEO ? (
        <video
            key={file.url}
            src={file.url}
            controls
            playsInline
            preload="metadata"
            aria-label={title}
            className={className}
        />
    ) : (
        <img src={file.url} alt={title} loading="lazy" className={className} />
    );

const FeatureGuideline = ({
    feature,
    guideline,
    onBack,
}: {
    feature: IFeature;
    guideline: IFeatureGuideline;
    onBack: () => void;
}) => {
    const [activeStep, setActiveStep] = useState(0);
    const [mediaIndex, setMediaIndex] = useState(0);
    const [zoom, setZoom] = useState<IFeatureFile | null>(null);
    const dialog = useRef<HTMLDialogElement>(null);
    const steps = useRef<(HTMLElement | null)[]>([]);
    const media = feature.files ?? [];

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
                if (visible) setActiveStep(Number((visible.target as HTMLElement).dataset.step));
            },
            { rootMargin: '0px 0px -50% 0px' }
        );
        steps.current.forEach((step) => {
            if (step) observer.observe(step);
        });
        return () => observer.disconnect();
    }, [guideline]);

    useEffect(() => {
        if (zoom) dialog.current?.showModal();
        else dialog.current?.close();
    }, [zoom]);

    const jump = (index: number) => {
        setActiveStep(index);
        steps.current[index]?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start',
        });
    };

    return (
        <main className="w-full">
            <div className="mx-auto max-w-[1080px] space-y-6 p-6">
                <BackButton type="button" to={`/whats-new?open=${encodeURIComponent(feature._id)}`} />
                <header className="max-w-[900px] space-y-4">
                    <div className="flex items-center gap-3 text-sm text-grayscale-500">
                        <span className="rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary">
                            {getFeatureTypeLabel(feature.type)}
                        </span>
                        <span>
                            {feature.module} ·{' '}
                            {new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }).format(
                                new Date(feature.date)
                            )}
                        </span>
                    </div>
                    <h1>{feature.title}</h1>
                    <p className="whitespace-pre-wrap">{feature.description}</p>
                </header>
                {!!media.length && (
                    <section aria-label="Feature media" className="space-y-4">
                        <div className="rounded-2xl bg-primary/5 p-4 md:px-10 md:pt-8 md:pb-0">
                            <div className="relative overflow-hidden rounded-xl bg-card-color shadow-lg">
                                <Media
                                    file={media[mediaIndex]}
                                    title={feature.title}
                                    className="aspect-video max-h-[640px] w-full object-contain"
                                />
                                {media.length > 1 && (
                                    <>
                                        <button
                                            type="button"
                                            aria-label="Previous feature media"
                                            onClick={() =>
                                                setMediaIndex((index) => (index - 1 + media.length) % media.length)
                                            }
                                            className="absolute left-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white text-grayscale-800 shadow"
                                        >
                                            ‹
                                        </button>
                                        <button
                                            type="button"
                                            aria-label="Next feature media"
                                            onClick={() => setMediaIndex((index) => (index + 1) % media.length)}
                                            className="absolute right-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white text-grayscale-800 shadow"
                                        >
                                            ›
                                        </button>
                                    </>
                                )}
                                <span
                                    aria-live="polite"
                                    className="pointer-events-none absolute right-3 top-3 rounded-full bg-black/65 px-3 py-1 text-xs font-semibold text-white"
                                >
                                    {mediaIndex + 1} / {media.length}
                                </span>
                            </div>
                        </div>
                        <div className="flex gap-3 overflow-x-auto pb-2">
                            {media.map((file, index) => (
                                <button
                                    key={file._id ?? file.url}
                                    type="button"
                                    aria-label={`Show ${file.name || `media ${index + 1}`}`}
                                    aria-pressed={index === mediaIndex}
                                    onClick={() => setMediaIndex(index)}
                                    className={`relative h-20 w-32 shrink-0 overflow-hidden rounded-lg border-2 bg-card-color ${index === mediaIndex ? 'border-primary' : 'border-separator-color-light'}`}
                                >
                                    {file.type === FileTypeEnum.IMAGE ? (
                                        <img src={file.url} alt="" className="h-full w-full object-cover" />
                                    ) : (
                                        <>
                                            <video
                                                src={file.url}
                                                muted
                                                playsInline
                                                preload="metadata"
                                                aria-hidden="true"
                                                className="h-full w-full object-cover"
                                            />
                                            <span className="absolute inset-0 grid place-items-center text-white">
                                                <span className="grid h-7 w-7 place-items-center rounded-full bg-black/65">
                                                    ▶
                                                </span>
                                            </span>
                                        </>
                                    )}
                                </button>
                            ))}
                        </div>
                    </section>
                )}
                <div className="grid items-start gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
                    <nav aria-label="In this guide" className="lg:sticky lg:top-6">
                        <p className="px-3 pb-3 text-xs font-semibold tracking-wider text-grayscale-500">
                            IN THIS GUIDE
                        </p>
                        {guideline.steps.map((step, index) => (
                            <button
                                key={step._id}
                                type="button"
                                onClick={() => jump(index)}
                                aria-current={activeStep === index ? 'step' : undefined}
                                className={`flex w-full gap-3 rounded-lg px-3 py-2 text-left text-sm ${activeStep === index ? 'bg-primary/10 font-semibold text-primary' : 'text-grayscale-500 hover:bg-grayscale-100'}`}
                            >
                                <span>{index + 1}</span>
                                <span>{step.title}</span>
                            </button>
                        ))}
                    </nav>
                    <section className="min-w-0 space-y-6">
                        {guideline.steps.map((step, index) => (
                            <section
                                key={step._id}
                                ref={(element) => {
                                    steps.current[index] = element;
                                }}
                                data-step={index}
                                className="scroll-mt-6 space-y-4"
                                aria-labelledby={`guide-step-${step._id}`}
                            >
                                <div className="flex items-start gap-4">
                                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary font-bold text-white">
                                        {index + 1}
                                    </span>
                                    <div className="min-w-0 pt-1">
                                        <h6 id={`guide-step-${step._id}`}>{step.title}</h6>
                                        {step.description && (
                                            <p className="mt-2 whitespace-pre-wrap">{step.description}</p>
                                        )}
                                    </div>
                                </div>
                                {step.file && (
                                    <div className="rounded-xl border border-separator-color-light bg-card-color p-4 md:ml-[52px]">
                                        {step.file.type === FileTypeEnum.IMAGE ? (
                                            <button
                                                type="button"
                                                onClick={() => setZoom(step.file)}
                                                aria-label={`Enlarge image for ${step.title}`}
                                                className="block w-full cursor-zoom-in"
                                            >
                                                <Media
                                                    file={step.file}
                                                    title={step.title}
                                                    className="mx-auto max-h-[460px] max-w-full rounded-lg"
                                                />
                                            </button>
                                        ) : (
                                            <Media
                                                file={step.file}
                                                title={step.title}
                                                className="max-h-[460px] w-full rounded-lg"
                                            />
                                        )}
                                    </div>
                                )}
                            </section>
                        ))}
                        {!!guideline.notes?.length && (
                            <aside className="rounded-xl bg-card-color p-6 shadow-sm">
                                <h6>Notes</h6>
                                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-grayscale-500">
                                    {guideline.notes.map((note, index) => (
                                        <li key={index} className="whitespace-pre-wrap">
                                            {note}
                                        </li>
                                    ))}
                                </ul>
                            </aside>
                        )}
                        <div className="flex flex-wrap gap-3">
                            <Button variant="shaded" size="sm" onClick={onBack}>
                                Back to What’s New
                            </Button>
                        </div>
                    </section>
                </div>
            </div>
            <dialog
                ref={dialog}
                onClose={() => setZoom(null)}
                onClick={(event) => {
                    if (event.target === event.currentTarget) setZoom(null);
                }}
                aria-label="Enlarged guide image"
                className="max-h-[90vh] max-w-[90vw] rounded-xl bg-card-color p-4 backdrop:bg-black/70"
            >
                <button
                    type="button"
                    autoFocus
                    onClick={() => setZoom(null)}
                    className="mb-3 block rounded-lg px-3 py-2 text-sm font-semibold"
                >
                    Close
                </button>
                {zoom && <img src={zoom.url} alt={zoom.name} className="max-h-[75vh] max-w-full object-contain" />}
            </dialog>
        </main>
    );
};

export default FeatureGuideline;
